import * as React from 'react';
import Typography from '@mui/material/Typography';
import { Box } from '@mui/material';
import {
    useGetLastViewedDashboards,
    LAST_VIEWED_DASHBOARDS_QUERY_KEY,
} from 'routes/Home/hooks/useGetLastViewedDashboards';
import { FAVOURITE_DASHBOARDS_QUERY_KEY } from 'routes/Home/hooks/favouriteDashboards';
import { DashboardT } from 'business/definitions';
import { useQueryClient } from '@tanstack/react-query';
import DashboardCard from 'ui/Card/DashboardCard';
import { syncCacheOnPropertyChange } from '../helpers';
import Button from '@mui/material/Button';
import Loading from 'components/Loading';
import { useTranslation } from 'react-i18next';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { DASHBOARDS_PAGE_URL } from 'components/App/components/routes';
import Flex from 'ui/Flex/Flex';

type Props = {
    title: string;
};

const RecentlyViewedDashboards = ({ title }: Props): JSX.Element => {
    const { data: dashboards, isLoading } = useGetLastViewedDashboards();
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    const onPropertyChange = (
        dashboard: DashboardT,
        property: keyof DashboardT,
        value: boolean,
    ) =>
        syncCacheOnPropertyChange(dashboard, property, value, queryClient, [
            FAVOURITE_DASHBOARDS_QUERY_KEY,
            LAST_VIEWED_DASHBOARDS_QUERY_KEY,
        ]);

    return (
        <Box sx={{ width: '100%' }} data-testid="recently-viewed-dashboards">
            <Typography variant="h5" sx={{ mb: '1.5rem' }}>
                {title}
            </Typography>
            {isLoading ? (
                <Loading />
            ) : dashboards?.length === 0 ? (
                <Flex
                    direction="column"
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                >
                    <Typography>
                        {t('dashboards.noRecentlyViewedDashboards')}
                    </Typography>
                    <Button
                        href={`${DASHBOARDS_PAGE_URL}?tab=sharedDashboards`}
                        endIcon={<ChevronRightIcon />}
                    >
                        {t('dashboards.noRecentlyViewedDashboardsButton')}
                    </Button>
                </Flex>
            ) : (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, 250px)',
                        justifyContent: 'space-evenly',
                        gap: 2,
                    }}
                >
                    {dashboards?.map((dashboard) => (
                        <Box
                            key={dashboard.slug}
                            data-testid={`dashboard-item ${dashboard.title}`}
                        >
                            <DashboardCard
                                dashboard={dashboard}
                                onPropertyChange={onPropertyChange}
                            />
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default RecentlyViewedDashboards;
